import { css } from 'styled-components';
import { theme, toRem } from 'design';
import { IButtonProps } from './ButtonProps';

const ButtonStyles = css<Partial<IButtonProps>>`
  position: relative;
  display: flex;
  flex-grow: 0;
  flex-shrink: 0;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;
  width: auto;
  height: ${toRem(theme.button.height.large)};
  padding: 0 ${toRem(theme.space.xl)};
  border-radius: ${toRem(theme.button.height.large / 2)};
  border: none;
  background-color: ${theme.color.primary[500]};
  font-family: ${theme.font.family.euclid};
  font-weight: ${theme.font.weight.semiBold};
  font-size: ${toRem(theme.font.size.sm)};
  text-decoration: none;
  color: ${theme.color.white};
  transition: background-color ${theme.transition.button.default};

  &:hover,
  &:focus-visible {
    background-color: ${theme.color.primary[600]};
    color: ${theme.color.white};
  }

  ${({ appearance }) =>
    appearance === 'destructive' &&
    css`
      background-color: ${theme.color.danger[50]};
      color: ${theme.color.white};

      &:hover,
      &:focus-visible {
        background-color: ${theme.color.danger[50]};
      }
    `}

  ${({ appearance }) =>
    appearance === 'secondary' &&
    css`
      border: ${toRem(1)} solid ${theme.color.neutral[200]};
      background-color: ${theme.color.neutral[100]};
      color: ${theme.color.neutral[900]};

      &:hover,
      &:focus {
        background-color: ${theme.color.neutral[100]};
        color: ${theme.color.neutral[900]};
      }
    `}

    ${({ appearance }) =>
    appearance === 'tertiary' &&
    css`
      background-color: ${theme.color.secondary[300]};
      color: ${theme.color.neutral[900]};

      &:hover,
      &:focus {
        background-color: ${theme.color.secondary[400]};
        color: ${theme.color.neutral[900]};
      }
    `}

      ${({ appearance, isProcessing }) =>
    appearance === 'white' &&
    css`
      border: ${toRem(1)} solid ${theme.color.neutral[300]};
      background-color: ${theme.color.white};
      color: ${theme.color.neutral[700]};

      &:hover,
      &:focus-visible {
        background-color: ${theme.color.white};
        border-color: ${theme.color.neutral[isProcessing ? 300 : 400]};
        color: ${theme.color.neutral[700]};
      }
    `}

    ${({ appearance }) =>
    appearance === 'transparent' &&
    css`
      background-color: transparent;
      color: ${theme.color.secondary[300]};

      &:hover,
      &:focus {
        background-color: ${theme.color.secondary[400]};
        color: ${theme.color.neutral[900]};
      }
    `}

    ${({ appearance }) =>
    appearance === 'link' &&
    css`
      background-color: transparent;
      color: ${theme.color.primary[600]};
      padding: 0;
      width: fit-content;
      height: auto;

      &:hover,
      &:focus {
        background-color: transparent;
        color: ${theme.color.primary[600]};
        text-decoration: underline;
      }
    `}

  ${({ size }) =>
    size === 'small' &&
    css`
      height: ${toRem(theme.button.height.small)};
      padding: 0 ${toRem(theme.space.lg)};
      border-radius: ${toRem(theme.button.height.small / 2)};
      font-size: ${toRem(theme.font.size.xxs)};
    `}

  ${({ disabled }) =>
    disabled &&
    css`
      background-color: ${theme.color.neutral[200]};
      color: ${theme.color.neutral[400]};
      border: none;
      cursor: auto;

      &:hover,
      &:focus {
        background-color: ${theme.color.neutral[200]};
        color: ${theme.color.neutral[400]};
      }

      svg {
        fill: ${theme.color.neutral[400]};
      }
    `}

    ${({ isLoading }) =>
    isLoading &&
    css`
      cursor: default;
    `}

      ${({ isProcessing }) =>
    isProcessing &&
    css`
      cursor: default;
    `}
`;

export default ButtonStyles;
